import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import { ROLE_LANDING_PAGES } from '../config/permissions';
import { openDB } from 'idb';
import cookieOnlySignalRService from '../services/signalRService'; // Import the cookie-only service
import { parseApiDate } from '../utils/helpers';
import { clearAllCache } from '../hooks/useDataCache';

const AuthContext = createContext();

const DB_NAME = 'AlarmAudioDB';
const STORE_NAME = 'audioFiles';
const TOKEN_REFRESH_INTERVAL = 8 * 60 * 1000;
const INACTIVITY_TIMEOUT = 7 * 24 * 60 * 60 * 1000;

async function initDB() {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  });
}

export const AuthProvider = ({ children }) => {
  // Only expose non-sensitive data
  const [authenticated, setAuthenticated] = useState(localStorage.getItem('isAuthenticated') === 'true');
  const [roleId, setRoleId] = useState(localStorage.getItem('roleId') || null);
  const [institutionName, setInstitutionName] = useState(localStorage.getItem('institutionName') || null);
  const [department, setDepartment] = useState(localStorage.getItem('department') || null);

  // Application state
  const [alarmSettings, setAlarmSettings] = useState(JSON.parse(localStorage.getItem('alarmSettings') || '[]'));
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signalRConnected, setSignalRConnected] = useState(false);

  // Refs
  const refreshTimerRef = useRef(null);
  const refreshingRef = useRef(false);
  const signalRUnsubscribeRef = useRef(null);
  const audioRef = useRef(null);
  const currentAudioUrlRef = useRef(null);
  const pendingPlayRef = useRef(null); // Stores masterAlarmId of a pending play that was blocked
  const audioUnlockedRef = useRef(false); // Tracks if browser audio has been unlocked by user gesture

  const navigate = useNavigate();
  const location = useLocation();

  // Alert management functions
  const clearAlert = (timestamp) => {
    setActiveAlerts((prev) => prev.filter(alert => alert.timestamp !== timestamp));
  };

  const clearAllAlerts = () => {
    setActiveAlerts([]);
  };

  const handleSignalRAlert = (alert) => {
    console.log('SignalR alert:', alert);

    let actualAlert = alert;
    if (alert['0'] && typeof alert['0'] === 'object') {
      actualAlert = alert['0'];
    }

    const { alertType, payload } = actualAlert;

    const alertTimestamp = parseApiDate(actualAlert.timestamp);
    const alertWithTimestamp = {
      ...actualAlert,
      timestamp: alertTimestamp ? alertTimestamp.toISOString() : new Date().toISOString(),
      id: Date.now() + Math.random()
    };

    if (alertType === 'Reset' || alertType === 'Confirmation' || alertType === 'Hold') {
      const bedId = payload?.bedId?.toString();
      if (bedId) {
        setActiveAlerts(prev => {
          // Filter out existing alarms for this bed
          const filteredItems = prev.filter(a => {
            const aPayload = a.payload || a['0']?.payload;
            return aPayload?.bedId?.toString() !== bedId;
          });
          // Add the control alert itself so other components can see it
          return [alertWithTimestamp, ...filteredItems];
        });
      } else {
        setActiveAlerts(prev => [alertWithTimestamp, ...prev]);
      }
    } else {
      setActiveAlerts(prev => [alertWithTimestamp, ...prev]);
    }
  };

  // Initialize SignalR - SUPER SIMPLE!
  const initializeSignalR = async () => {
    try {
      console.log('🔗 Initializing SignalR...');

      // Just start the connection - server handles everything else
      await cookieOnlySignalRService.startConnection();

      // Subscribe to alerts
      signalRUnsubscribeRef.current = cookieOnlySignalRService.subscribeToAlerts(handleSignalRAlert);

      setSignalRConnected(true);
      console.log('✅ SignalR initialized successfully');

    } catch (error) {
      console.error('❌ Failed to initialize SignalR:', error);
      setSignalRConnected(false);
    }
  };

  // Stop SignalR connection
  const stopSignalR = async () => {
    try {
      if (signalRUnsubscribeRef.current) {
        signalRUnsubscribeRef.current();
        signalRUnsubscribeRef.current = null;
      }

      await cookieOnlySignalRService.stopConnection();
      setSignalRConnected(false);

    } catch (error) {
      console.error('❌ Error stopping SignalR:', error);
    }
  };

  // Track user activity
  useEffect(() => {
    const trackUserActivity = () => {
      if (authenticated) {
        localStorage.setItem('lastActivity', Date.now().toString());
      }
    };

    window.addEventListener('mousemove', trackUserActivity);
    window.addEventListener('keydown', trackUserActivity);
    window.addEventListener('click', trackUserActivity);
    window.addEventListener('scroll', trackUserActivity);

    trackUserActivity();

    return () => {
      window.removeEventListener('mousemove', trackUserActivity);
      window.removeEventListener('keydown', trackUserActivity);
      window.removeEventListener('click', trackUserActivity);
      window.removeEventListener('scroll', trackUserActivity);
    };
  }, [authenticated]);

  // Audio unlock: play a silent sound on first user gesture to unlock browser autoplay
  useEffect(() => {
    if (!authenticated) return;

    const unlockAudio = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;

      // Play a tiny silent audio to unlock the audio context
      const silentAudio = new Audio(
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='
      );
      silentAudio.volume = 0;
      silentAudio.play().then(() => {
        console.log('✅ Audio context unlocked by user interaction');
        // If there is a pending alarm waiting to play, play it now
        if (pendingPlayRef.current) {
          playGlobalAlarm(pendingPlayRef.current);
          pendingPlayRef.current = null;
        } else if (audioRef.current && audioRef.current.paused) {
          // Resume any paused alarm audio
          audioRef.current.play().catch(e => console.warn('Resume failed:', e));
        }
      }).catch(() => {
        // Still blocked - leave pendingPlayRef set, it will retry on next gesture
        audioUnlockedRef.current = false;
      });

      // Only remove listeners after a successful unlock
      if (audioUnlockedRef.current) {
        ['click', 'keydown', 'touchstart'].forEach(ev =>
          document.removeEventListener(ev, unlockAudio)
        );
      }
    };

    ['click', 'keydown', 'touchstart'].forEach(ev =>
      document.addEventListener(ev, unlockAudio)
    );

    return () => {
      ['click', 'keydown', 'touchstart'].forEach(ev =>
        document.removeEventListener(ev, unlockAudio)
      );
    };
  }, [authenticated]);

  // Global priority-based audio management
  useEffect(() => {
    if (!authenticated) {
      stopCurrentAudio();
      return;
    }

    // Filter relevant alarms and find the highest priority
    const alarmAlerts = activeAlerts.filter(a => {
      const type = a.alertType || a['0']?.alertType;
      const payload = a.payload || a['0']?.payload;
      // ONLY treat as alarms if they are NOT control alerts and HAVE a masterAlarmId
      return type !== 'SOS' &&
        type !== 'Confirmation' &&
        type !== 'Hold' &&
        type !== 'Reset' &&
        payload?.masterAlarmId;
    });

    if (alarmAlerts.length === 0) {
      stopCurrentAudio();
      return;
    }

    // Determine highest priority (lower number = higher priority)
    // Use admin-configured priority from alarmSettings as source of truth
    let highestPriorityAlarm = null;
    let minPriority = Infinity;

    alarmAlerts.forEach(a => {
      const payload = a.payload || a['0']?.payload;
      const masterAlarmId = payload?.masterAlarmId;
      // Look up priority from settings first (admin configured), fall back to payload
      const settingPriority = alarmSettings.find(s => s.masterAlarmId === masterAlarmId)?.priority;
      const priority = settingPriority != null ? Number(settingPriority) : Number(payload?.alarmPriority || 999);
      if (priority < minPriority) {
        minPriority = priority;
        highestPriorityAlarm = a;
      }
    });

    if (highestPriorityAlarm) {
      const payload = highestPriorityAlarm.payload || highestPriorityAlarm['0']?.payload;
      playGlobalAlarm(payload.masterAlarmId);
    }
  }, [activeAlerts, authenticated, alarmSettings]);

  const stopCurrentAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (currentAudioUrlRef.current) {
      URL.revokeObjectURL(currentAudioUrlRef.current);
      currentAudioUrlRef.current = null;
    }
    pendingPlayRef.current = null;
  };

  const playGlobalAlarm = async (masterAlarmId) => {
    const setting = alarmSettings.find(s => s.masterAlarmId === masterAlarmId);
    if (!setting) return;

    // Check if already playing this tune
    if (audioRef.current && audioRef.current.dataset.masterAlarmId === masterAlarmId.toString()) {
      return;
    }

    try {
      stopCurrentAudio();

      const blob = await getAudioBlob(setting.tunePath);
      if (blob) {
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);
        audio.loop = true;
        audio.dataset.masterAlarmId = masterAlarmId;
        audio.dataset.alarmAudio = 'true';

        currentAudioUrlRef.current = audioUrl;
        audioRef.current = audio;

        audio.play().catch(err => {
          // Browser blocked autoplay (common on reload before user interaction)
          console.warn('Audio playback blocked by browser, will retry on user interaction', err);
          pendingPlayRef.current = masterAlarmId;

          // Register a one-time listener to resume audio on first interaction
          const resumeAudio = () => {
            if (audioRef.current && pendingPlayRef.current === masterAlarmId) {
              audioRef.current.play().catch(e => console.warn('Still blocked:', e));
              pendingPlayRef.current = null;
            }
            ['click', 'keydown', 'touchstart'].forEach(ev =>
              document.removeEventListener(ev, resumeAudio)
            );
          };
          ['click', 'keydown', 'touchstart'].forEach(ev =>
            document.addEventListener(ev, resumeAudio, { once: true })
          );
        });
      }
    } catch (err) {
      console.error('Error in playGlobalAlarm:', err);
    }
  };

  // Initialize SignalR when authenticated
  useEffect(() => {
    if (authenticated && roleId) {
      initializeSignalR();
    } else if (!authenticated) {
      stopSignalR();
      stopCurrentAudio();
    }

    return () => {
      stopSignalR();
      stopCurrentAudio();
    };
  }, [authenticated, roleId]);

  // Token refresh logic (unchanged)
  const refreshToken = async (forcedRefresh = false) => {
    if (refreshingRef.current) return true;
    refreshingRef.current = true;

    try {
      if (forcedRefresh) console.log('Proactively refreshing token');
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await axiosInstance.post('/Account/refresh-token');
          if (response.data.result === true) {
            refreshingRef.current = false;
            // Reinitialize SignalR with new token if needed
            if (authenticated && signalRConnected) {
              await stopSignalR();
              setTimeout(initializeSignalR, 1000);
            }
            return true;
          } else {
            const errorMessage = response.data.errorResponse?.message || '';
            if (errorMessage.includes("Invalid token") || errorMessage.includes("inactive for too long")) {
              console.error('Permanent token refresh failure:', errorMessage);
              refreshingRef.current = false;
              return false;
            }
            console.warn('Temporary token refresh issue, retrying:', errorMessage);
            if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          }
        } catch (attemptError) {
          if (!attemptError.response && attempt < 2) {
            console.warn('Network error during refresh, retrying', attemptError);
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          } else {
            throw attemptError;
          }
        }
      }
      console.error('All token refresh attempts failed');
      refreshingRef.current = false;
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      refreshingRef.current = false;
      if (error.response?.status === 401) return false;
      return true;
    }
  };

  // Token refresh timer (unchanged)
  useEffect(() => {
    if (authenticated) {
      const checkInactivity = () => {
        const lastActivity = localStorage.getItem('lastActivity');
        if (lastActivity && (Date.now() - parseInt(lastActivity)) > INACTIVITY_TIMEOUT) {
          console.log('User inactive for too long, logging out');
          logout();
          return false;
        }
        return true;
      };

      if (!checkInactivity()) return;

      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      refreshToken(true);

      refreshTimerRef.current = setInterval(async () => {
        if (!checkInactivity()) return;
        const isSuccess = await refreshToken(true);
        if (!isSuccess) {
          console.log('Proactive token refresh failed, logging out');
          logout();
        }
      }, TOKEN_REFRESH_INTERVAL);

      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      };
    }
  }, [authenticated]);

  // All your existing methods remain the same...
  const fetchHeaderDetails = async () => {
    try {
      const response = await axiosInstance.get('/Account/get-header-details', { withCredentials: true });
      if (response.data.result) return response.data.result;
      return null;
    } catch (error) {
      console.error('Failed to fetch header details:', error);
      return null;
    }
  };

  const fetchAndStoreAlarmSettings = async () => {
    try {
      const response = await axiosInstance.get('/alarm-settings/get');
      if (response.data.result) {
        const filteredSettings = response.data.result.map(setting => ({
          masterAlarmId: setting.masterAlarmId,
          alarmName: setting.alarmType,
          color: setting.color,
          tunePath: setting.tunePath,
          priority: setting.priority, // Store priority from admin settings
        }));
        localStorage.setItem('alarmSettings', JSON.stringify(filteredSettings));
        setAlarmSettings(filteredSettings);
        await cacheAudioFiles(filteredSettings);
      } else {
        throw new Error(response.data.errorResponse?.message || 'Failed to fetch alarm settings');
      }
    } catch (err) {
      console.error('Error fetching alarm settings:', err);
      localStorage.setItem('alarmSettings', '[]');
      setAlarmSettings([]);
    }
  };

  const cacheAudioFiles = async (settings) => {
    const requiredTunePaths = ["sos.mp3", "extension.mp3"];
    const updatedSettings = [...settings, ...requiredTunePaths.map(tunePath => ({ tunePath }))];
    const uniqueTunePaths = [...new Set(updatedSettings.map(setting => setting.tunePath))];

    for (const tunePath of uniqueTunePaths) {
      try {
        let db = await initDB();
        let tx = db.transaction(STORE_NAME, 'readonly');
        let store = tx.objectStore(STORE_NAME);
        const cachedAudio = await store.get(tunePath);
        if (cachedAudio) continue;

        console.log(`Fetching audio file: ${tunePath}`);
        const response = await axiosInstance.get(`/Uploads/Tunes/${tunePath}`, { responseType: 'blob' });
        if (!response.data || !(response.data instanceof Blob)) throw new Error(`Invalid response for ${tunePath}: Not a blob`);

        db = await initDB();
        tx = db.transaction(STORE_NAME, 'readwrite');
        store = tx.objectStore(STORE_NAME);
        await store.put(response.data, tunePath);
        console.log(`Cached audio file: ${tunePath}`);
      } catch (err) {
        console.error(`Error caching audio file ${tunePath}:`, err);
      }
    }
  };

  const getAudioBlob = async (tunePath) => {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const blob = await store.get(tunePath);
    await tx.done;
    return blob;
  };

  const playAlarm = async (masterAlarmId) => {
    const settings = JSON.parse(localStorage.getItem('alarmSettings') || '[]');
    const setting = settings.find(s => s.masterAlarmId === masterAlarmId);
    if (!setting) {
      console.error('No alarm setting found for masterAlarmId:', masterAlarmId);
      return;
    }
    try {
      const blob = await getAudioBlob(setting.tunePath);
      if (blob) {
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);
        audio.play().catch(err => console.error('Error playing audio:', err));
        audio.onended = () => URL.revokeObjectURL(audioUrl);
      } else {
        console.error('Audio file not found in cache:', setting.tunePath);
        const response = await axiosInstance.get(`/Uploads/tunes/${setting.tunePath}`, { responseType: 'blob' });
        const audioUrl = URL.createObjectURL(response.data);
        const audio = new Audio(audioUrl);
        audio.play().catch(err => console.error('Error playing audio:', err));
        audio.onended = () => URL.revokeObjectURL(audioUrl);
      }
    } catch (err) {
      console.error('Error retrieving audio from cache:', err);
    }
  };

  const login = async (username, institutionNumber, password) => {
    const payload = { userName: username, institutionNumber: institutionNumber, password };
    try {
      const response = await axiosInstance.post('/Account/login', payload);
      if (response.data.result) {
        const headerDetails = await fetchHeaderDetails();
        if (headerDetails) {
          const stringRoleId = headerDetails.roleId?.toString();

          setAuthenticated(true);
          setRoleId(stringRoleId);
          setInstitutionName(headerDetails.institutionName);
          setDepartment(headerDetails.department);

          localStorage.setItem('roleId', stringRoleId);
          localStorage.setItem('userId', headerDetails.userId || '');
          localStorage.setItem('supervisorId', headerDetails.supervisorId || '');
          localStorage.setItem('institutionName', headerDetails.institutionName || '');
          localStorage.setItem('department', headerDetails.department || '');
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('lastActivity', Date.now().toString());

          await fetchAndStoreAlarmSettings();

          const landingPage = ROLE_LANDING_PAGES[stringRoleId] || '/';
          navigate(landingPage);
        } else {
          throw { message: 'Could not extract header details' };
        }
      } else {
        throw { message: response.data.errorResponse?.message || 'Login failed' };
      }
    } catch (error) {
      console.log('Login error:', error);
      setAuthenticated(false);
      setRoleId(null);
      setInstitutionName(null);
      setDepartment(null);
      setAlarmSettings([]);
      localStorage.clear();
      throw { message: error?.message || error?.response?.data?.errorResponse?.message || 'Login failed' };
    }
  };
  async function clearAllCaches() {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    console.log('All caches cleared');
  }
  const logout = async () => {
    try {
      await axiosInstance.post('/Account/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      await stopSignalR();
      try {
        await clearAllCaches();
      } catch (err) {
        console.error('Error clearing caches:', err);
      }
      // Clear the in-memory stale-while-revalidate cache
      clearAllCache();
      setAuthenticated(false);
      setRoleId(null);
      setInstitutionName(null);
      setDepartment(null);
      setAlarmSettings([]);
      setActiveAlerts([]);
      localStorage.clear();

      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      refreshingRef.current = false;
      navigate('/auth/login');
    }
  };

  // ==================================================================
  // =========== UPDATED AUTHENTICATION VERIFICATION LOGIC ============
  // ==================================================================
  useEffect(() => {
    const verifyAuthentication = async () => {
      setIsLoading(true);

      const isUserAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      const isAuthRoute = location.pathname.startsWith('/auth');
      const isPublicRoute = location.pathname === '/terms'; // Public static routes

      // Scenario A: User is LOGGED IN but trying to access an AUTH page (like /login)
      // Action: Redirect them to their designated landing page.
      if (isUserAuthenticated && isAuthRoute) {
        const storedRoleId = localStorage.getItem('roleId');
        const landingPage = ROLE_LANDING_PAGES[storedRoleId] || '/';
        console.log("Authenticated user on auth page, redirecting to:", landingPage);
        navigate(landingPage);
        setIsLoading(false);
        return;
      }

      // Scenario B: User is NOT LOGGED IN and trying to access a PROTECTED page.
      // Action: Redirect them to the login page. Skip public routes like /terms.
      if (!isUserAuthenticated && !isAuthRoute && !isPublicRoute) {
        console.log("Unauthenticated user on protected page, redirecting to login.");
        navigate('/auth/login');
        setIsLoading(false);
        return;
      }

      // Scenario B2: Unauthenticated user on a public static route — allow through.
      if (!isUserAuthenticated && isPublicRoute) {
        setIsLoading(false);
        return;
      }

      // Scenario C: User is LOGGED IN and on a PROTECTED page.
      // Action: This is the main verification path. Check inactivity, tokens, etc.
      if (isUserAuthenticated && !isAuthRoute) {
        try {
          const lastActivity = localStorage.getItem('lastActivity');
          const inactive = lastActivity && (Date.now() - parseInt(lastActivity)) > INACTIVITY_TIMEOUT;

          if (inactive) {
            console.log('User inactive for 7 days, forcing logout');
            await logout();
            setIsLoading(false);
            return;
          }

          // Verify backend session is still valid
          const headerDetails = await fetchHeaderDetails();
          if (headerDetails) {
            const stringRoleId = headerDetails.roleId?.toString();
            // Refresh local state from fetched details
            setAuthenticated(true);
            setRoleId(stringRoleId);
            setInstitutionName(headerDetails.institutionName);
            setDepartment(headerDetails.department);
            // Also update localStorage to be sure
            localStorage.setItem('roleId', stringRoleId);
            // Proactively refresh the token and alarm settings to keep session alive
            await refreshToken(true);
            // Refresh alarm settings on reload so priority/color changes from admin are picked up
            fetchAndStoreAlarmSettings().catch(e => console.warn('Could not refresh alarm settings:', e));
          } else {
            throw new Error('Failed to verify header details, session may be invalid.');
          }
        } catch (error) {
          console.error('Auth verification failed, logging out:', error);
          await logout();
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Scenario D: User is NOT LOGGED IN and on an AUTH page (like /login or /change-password).
      // Action: Do nothing. Allow them to see the page.
      setIsLoading(false);
    };

    verifyAuthentication();
    // CRITICAL: This effect MUST run on every location change.
  }, [location.pathname, navigate]);

  if (isLoading) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8e8e8' }}>
        <div style={{ width: '100%', maxWidth: 900 }}>
          <div style={{ textAlign: 'center', marginBottom: 24, fontSize: 14, color: '#999' }}>Verifying session...</div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      authenticated,
      roleId,
      institutionName,
      department,
      alarmSettings,
      activeAlerts,
      clearAlert,
      clearAllAlerts,
      signalRConnected,
      login,
      logout,
      playAlarm,
      setActiveAlerts
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
